import { Typography } from "@mui/material";
import { type IAllProps, Editor as TinyMCEEditor } from "@tinymce/tinymce-react";
import { useCallback, useEffect, useRef, useState } from "react";
import "tinymce/tinymce";
import "tinymce/models/dom/model";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/plugins/autoresize";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/advlist";
import "tinymce/plugins/wordcount";
import "./langs/fr_FR";
import "./plugin.variables";
import { InfoHelper } from "@legacy/InfoHelper";

const parseContentToFindVariables = (content: string) => {
  const regex = /\[\[(.+?)\]\]/gm;
  let m: RegExpExecArray | null;
  const variablesFound: string[] = [];
  // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
  while ((m = regex.exec(content)) !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (m.index === regex.lastIndex) {
      regex.lastIndex++;
    }

    m.forEach((match, groupIndex) => {
      if (groupIndex === 1) {
        variablesFound.push(match);
      }
    });
  }
  return variablesFound;
};

export interface IEditorProps extends Omit<IAllProps, "onChange"> {
  id?: string;
  value?: string;
  enableVariables?: boolean;
  hideVariablesHelp?: boolean;
  onChange: (value: string, _newVars?: string[], _textValue?: string) => void;
  onSelectVariable?: (value: string) => void;
  output?: "html" | "text";
  height?: number;
  maxHeight?: number;
  placeHolderText?: string;
  width?: number | string;
  fullWidth?: boolean;
  noToolbar?: boolean;
  lengthIndicator?: number;
}

/**
 * @deprecated Use `EditorV2` from `@inputs/text/EditorV2` instead.
 */
export const Editor = (props: IEditorProps) => {
  const {
    id,
    value,
    enableVariables,
    hideVariablesHelp,
    onChange,
    onSelectVariable,
    output = "html",
    height = 144,
    maxHeight,
    placeHolderText,
    width = 640,
    fullWidth,
    noToolbar,
    lengthIndicator,
    disabled,
    ...rest
  } = props;
  const [editorValue, setEditorValue] = useState("");
  const editorRef = useRef<Exclude<TinyMCEEditor["editor"], undefined> | null>(null);

  const handleChange = useCallback(
    (newValue: string, editor: Exclude<TinyMCEEditor["editor"], undefined>) => {
      if ((onChange && newValue) || newValue === "") {
        let newVars: string[] | undefined;

        if (enableVariables) {
          newVars = parseContentToFindVariables(newValue);
        }

        if (output === "text") {
          onChange(editor.getContent(), newVars, editor.getContent({ format: "text" }));
        } else {
          onChange(newValue, newVars);
        }
      }
    },
    [enableVariables, onChange, output]
  );

  useEffect(() => {
    if (value) {
      setEditorValue(value);
    }
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (value && value !== editorValue) {
      setEditorValue(value);
    }
  }, [value]);
  const toolbar = "bold italic underline strikethrough numlist bullist link removeformat wordcount";
  const plugins = ["autoresize", "lists", "advlist", "link", "wordcount"];

  if (enableVariables) {
    plugins.push("variables_list");
  }

  useEffect(() => {
    if (editorRef.current) {
      if (disabled) {
        editorRef.current.contentDocument.body.style.opacity = "0.42";
      } else {
        editorRef.current.contentDocument.body.style.opacity = "1";
      }
    }
  }, [disabled]);

  return (
    <>
      <TinyMCEEditor
        id={id}
        value={editorValue}
        onEditorChange={(content, editor) => {
          setEditorValue(content);
          handleChange(content, editor);
        }}
        data-testid={"tinymceEditor-test-id"}
        {...rest}
        disabled={disabled}
        plugins={plugins}
        toolbar={noToolbar ? "" : toolbar}
        // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
        onInit={(_, editor) => (editorRef.current = editor)}
        init={{
          width: fullWidth ? "100%" : width,
          // selector: "textarea",
          placeholder: placeHolderText,
          base_url: window.location.href,
          min_height: height,
          max_height: maxHeight,
          branding: false,
          menubar: false,
          statusbar: false,
          icons: "material",
          icons_url: "https://fonts.bimbamjob.fr/tinymce/icons/material/icons.js",
          skin_url: "https://fonts.bimbamjob.fr/tinymce/skins/BBJMaterial",
          content_css: "https://fonts.bimbamjob.fr/tinymce/content.min.css",
          content_css_core: true,
          // CHANGE STYLE INSIDE OF IFRAME HERE
          content_style:
            "html { height: 100% !important; display: flex;}" +
            'body { font-family: "Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif; font-size: 14px; width: 100%}' +
            "p {margin: 0}" +
            `.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
		          color: rgba(0, 0, 0, 0.3);
              cursor: text;
	            }`,
          language: "fr_FR",
          entity_encoding: "raw",
          // Unset document_base_url + set to true relative_url mean load file from component.
          relative_url: true,
          document_base_url: undefined,
          theme: import.meta.env.NODE_ENV === "test" ? false : undefined,
          setup: (e) => {
            if (enableVariables && onSelectVariable) {
              e.on("SelectVariable", (item: string) => onSelectVariable(item));
            }
          },
          contextmenu: "",
        }}
      />
      {lengthIndicator && (
        <Typography align="right" variant="body1">
          Nombre de caractères: {lengthIndicator}
        </Typography>
      )}
      {enableVariables && !hideVariablesHelp && (
        <InfoHelper type="informations">
          <span>
            Utilisez la touche <b>[</b> pour sélectionner une variable à ajouter au template.
          </span>
        </InfoHelper>
      )}
    </>
  );
};
